import { gsap } from 'gsap';

class CounterAnimation {
    constructor(selector = '.animate-counter', duration = 1.5) {
        this.selector = selector;
        this.duration = duration;
        this.staggerDelay = 0.25;
        this.observer = null;
    }

    init() {
        this.elements = document.querySelectorAll(this.selector);

        if (!this.elements.length) {
            return;
        }

        this.elements.forEach((element) => this.setInitialState(element));

        if (!('IntersectionObserver' in window)) {
            this.elements.forEach((element, index) => this.animate(element, index));
            return;
        }

        this.observer = new IntersectionObserver((entries, observer) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) {
                    return;
                }

                const element = entry.target;
                const index = Array.from(this.elements).indexOf(element);

                this.animate(element, index);
                observer.unobserve(element);
            });
        }, {
            threshold: 0.25,
            rootMargin: '0px 0px -10% 0px',
        });

        this.elements.forEach((element) => this.observer.observe(element));
    }

    setInitialState(element) {
        const originalText = element.textContent.trim();

        if (!originalText) {
            return;
        }

        element.dataset.counterTarget = originalText;
        element.innerHTML = '&nbsp;';
    }

    animate(element, index) {
        const rawText = element.dataset.counterTarget || element.textContent.trim();
        const hasPlus = rawText.includes('+');
        const numericValue = Number.parseInt(rawText.replace(/[^0-9-]/g, ''), 10);

        if (Number.isNaN(numericValue)) {
            return;
        }

        const formatValue = (value) => {
            const formattedNumber = new Intl.NumberFormat('en-US', {
                maximumFractionDigits: 0,
                minimumFractionDigits: 0,
            }).format(value);

            return `${hasPlus && value >= 0 ? '+' : ''}${formattedNumber}`;
        };

        const state = { value: 0 };

        gsap.to(state, {
            value: numericValue,
            duration: this.duration,
            delay: index * this.staggerDelay,
            ease: 'power1.out',
            onUpdate: () => {
                const currentValue = Math.round(state.value);
                const formatted = new Intl.NumberFormat('en-US', {
                    maximumFractionDigits: 0,
                    minimumFractionDigits: 0,
                }).format(currentValue);

                element.textContent = formatted;
            },
            onComplete: () => {
                element.textContent = formatValue(numericValue);
                gsap.fromTo(element, { scale: 1 }, { scale: 1.1, duration: 0.12, yoyo: true, repeat: 1, ease: 'power1.inOut' });
            },
        });
    }
}

export default new CounterAnimation();
